import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Transportation.svg";
import WhatsAppButton from "../components/WhatsappButton";

const TransportationPolicy: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen " style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative text-black py-20 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute lg:bottom-[-5rem] bottom-[-2rem] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[350px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6">
                {t("transportationPolicy.title")}
              </h1>
              <p className="text-xl md:text-2xl font-light text-natural-800 mb-8">
                {t("transportationPolicy.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                {t("transportationPolicy.intro")}
              </p>
              <p className="text-lg text-neutral-800 leading-relaxed">
                {t("transportationPolicy.intro2")}
              </p>
            </div>

            {/* Section 1 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section1.title")}
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("transportationPolicy.section1.description")}
              </h3>
              <ul className="space-y-3 mb-6">
                {t<string[]>("transportationPolicy.section1.features").map(
                  (feature: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section1.note")}
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section2.title")}
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("transportationPolicy.section2.description")}
              </h3>
              <ul className="space-y-3 mb-6">
                {t<string[]>("transportationPolicy.section2.requirements").map(
                  (requirement: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">
                        {requirement}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section2.note")}
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section3.title")}
              </h2>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("transportationPolicy.section3.arrival.title")}
                </h3>
                <ul className="space-y-3">
                  {t<string[]>(
                    "transportationPolicy.section3.arrival.points"
                  ).map((point: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("transportationPolicy.section3.shelter.title")}
                </h3>
                <ul className="space-y-3">
                  {t<string[]>(
                    "transportationPolicy.section3.shelter.points"
                  ).map((point: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section3.note")}
                </p>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section4.title")}
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("transportationPolicy.section4.description")}
              </h3>
              <ul className="space-y-3 mb-6">
                {t<string[]>("transportationPolicy.section4.features").map(
                  (feature: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section4.note")}
                </p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section5.title")}
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("transportationPolicy.section5.description")}
              </h3>
              <ul className="space-y-3 mb-6">
                {t<string[]>("transportationPolicy.section5.features").map(
                  (feature: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section5.note")}
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div className="bg-[#FBEACD] rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-semibold text-[#492A19] mb-6">
                {t("transportationPolicy.section6.title")}
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {t("transportationPolicy.section6.description")}
              </h3>
              <ul className="space-y-3 mb-6">
                {t<string[]>("transportationPolicy.section6.features").map(
                  (feature: string, index: number) => (
                    <li key={index}>
                      <span className="text-neutral-800 text-center">
                        {feature}
                      </span>
                    </li>
                  )
                )}
              </ul>
              <div className="bg-orange-50 border-l-4 rounded-2xl border-[#492A19] p-4">
                <p className="text-neutral-800 italic">
                  {t("transportationPolicy.section6.note")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />

      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default TransportationPolicy;
